import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Stack, Box } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import styled from "@emotion/styled";
import MKTypography from "components/MKTypography";
import HeaderContainer from "styles/components/headerContainer";
import { StyledSectionContainer } from "styles/components/SectionContainer/index.styles";
import { StyledImage } from "styles/components/imageContainer/index.styles";
import { StyledPreparationCard } from "index.styles";
import theme from "assets/theme";
import useMediaQueries from "utils/mediaQueries.utils";

const cardImageCssProps = {
  display: "block",
  width: "100%",
  height: "275px",
  objectFit: "cover",
};

const InlineContentLink = styled(Link)(({ theme: t }) => ({
  color: t.palette.accent.main,
  textDecoration: "underline",
  "&:hover": { color: t.palette.primary.main },
}));

const ToggleLink = styled("button")(({ theme: t }) => ({
  color: t.palette.accent.main,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.25rem",
  fontFamily: t.typography.fontFamily,
  fontWeight: 600,
  fontSize: "1rem",
  lineHeight: 1.6,
  background: "none",
  border: "none",
  padding: 0,
  [t.breakpoints.up("lg")]: {
    fontSize: "1.125rem",
    lineHeight: 1.625,
  },
  "&:hover": { color: t.palette.primary.main },
  "&:focus": { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: "2px" },
  "&:active": { opacity: 0.6 },
}));

const renderSegments = (segments) =>
  segments.map((seg, i) => {
    if (seg.link) {
      return (
        <InlineContentLink
          key={i}
          to={seg.link}
          target={seg.external ? "_blank" : undefined}
          rel={seg.external ? "noopener noreferrer" : undefined}
        >
          {seg.text}
        </InlineContentLink>
      );
    }
    return <React.Fragment key={i}>{seg.text}</React.Fragment>;
  });

const renderItem = (item, index, isLg) => {
  const bodyVariant = isLg ? "body1" : "body2";

  if (typeof item === "string") {
    return (
      <MKTypography key={index} component="p" variant={bodyVariant} gutterBottom>
        {item}
      </MKTypography>
    );
  }
  if (item.heading && item.body) {
    return (
      <Box key={index} sx={{ marginBottom: "1rem" }}>
        <MKTypography component="h4" variant={bodyVariant} sx={{ display: "inline", fontWeight: 700, margin: 0 }}>
          {item.heading}
        </MKTypography>{" "}
        <MKTypography component="span" variant={bodyVariant}>
          {item.body}
        </MKTypography>
      </Box>
    );
  }
  if (item.heading && item.segments) {
    return (
      <Box key={index} sx={{ marginBottom: "1rem" }}>
        <MKTypography component="h4" variant={bodyVariant} sx={{ display: "inline", fontWeight: 700, margin: 0 }}>
          {item.heading}
        </MKTypography>{" "}
        <MKTypography component="span" variant={bodyVariant}>
          {renderSegments(item.segments)}
        </MKTypography>
      </Box>
    );
  }
  if (item.segments) {
    return (
      <MKTypography key={index} component="p" variant={bodyVariant} gutterBottom>
        {renderSegments(item.segments)}
      </MKTypography>
    );
  }
  if (item.list) {
    return (
      <Box key={index} component="ul" sx={{ margin: "0 0 1rem 0", paddingLeft: "2.5rem", display: "flow-root" }}>
        {item.list.map((b, i) => (
          <MKTypography key={i} component="li" variant={bodyVariant}>
            {typeof b === "string" ? b : renderSegments(b.segments)}
          </MKTypography>
        ))}
      </Box>
    );
  }
  return null;
};

const BeforeYourSession = ({ title, highlightText, cards = [] }) => {
  const { isLg } = useMediaQueries();
  const [expanded, setExpanded] = useState(() => cards.map(() => false));

  const toggle = (i) => setExpanded((prev) => prev.map((v, idx) => (idx === i ? !v : v)));

  const defaultVisible = isLg ? 2 : 1;

  return (
    <StyledSectionContainer component="section" id="before-your-session-section">
      <HeaderContainer
        textColor={theme.palette.primary.main}
        highlightColor={theme.palette.accent.main}
        component="h2"
        variant="h2"
        title={title}
        highlightText={highlightText}
        cssProps={{ margin: "0 0 2rem 0" }}
      />
      <Stack spacing={"2rem"}>
        {cards.map(({ title: cardTitle, description, image, alt }, index) => {
          const items = Array.isArray(description) ? description : description != null ? [description] : [];
          const isExpanded = expanded[index];
          const initialItems = items.slice(0, defaultVisible);
          const overflowItems = items.slice(defaultVisible);
          const hasMore = overflowItems.length > 0;
          const overflowId = `before-card-overflow-${index}`;

          const toggleEl = hasMore ? (
            <ToggleLink
              type="button"
              onClick={() => toggle(index)}
              aria-expanded={isExpanded}
              aria-controls={overflowId}
              aria-label={isExpanded ? `View less for ${cardTitle}` : `View more for ${cardTitle}`}
            >
              {isExpanded ? (
                <>
                  <KeyboardArrowUp sx={{ fontSize: "1.1rem" }} />
                  View Less
                </>
              ) : (
                <>
                  <KeyboardArrowDown sx={{ fontSize: "1.1rem" }} />
                  View More
                </>
              )}
            </ToggleLink>
          ) : null;

          const titleRow = (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ marginBottom: "1rem", ...(isExpanded && { overflow: "hidden" }) }}
            >
              <Stack direction="row" alignItems="center" spacing={".5rem"}>
                <img
                  src={"https://d3a0rs3velfrz.cloudfront.net/check.svg"}
                  alt="Check mark icon"
                  width={isLg ? "40px" : "25px"}
                  height={isLg ? "40px" : "25px"}
                />
                <MKTypography component="h3" variant="h6">
                  {cardTitle}
                </MKTypography>
              </Stack>
              {isLg && toggleEl}
            </Stack>
          );

          return (
            <StyledPreparationCard key={index}>
              {isExpanded ? (
                <Box sx={{ "&::after": { content: '""', display: "table", clear: "both" } }}>
                  <Box
                    sx={{
                      float: { lg: "left" },
                      width: { xs: "100%", lg: "268px" },
                      marginRight: { lg: "1.5rem" },
                      marginBottom: "1rem",
                    }}
                  >
                    <StyledImage src={image} alt={alt} cssProps={cardImageCssProps} />
                  </Box>
                  {titleRow}
                  {initialItems.map((item, i) => renderItem(item, i, isLg))}
                  <Box id={overflowId} aria-hidden={false}>
                    {overflowItems.map((item, i) => renderItem(item, i + defaultVisible, isLg))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", lg: "row" },
                    gap: { xs: "1rem", lg: "1.5rem" },
                    alignItems: { lg: "center" },
                  }}
                >
                  <Box sx={{ flexShrink: 0, alignSelf: { lg: "flex-start" }, width: { xs: "100%", lg: "268px" } }}>
                    <StyledImage src={image} alt={alt} cssProps={cardImageCssProps} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {titleRow}
                    {initialItems.map((item, i) => renderItem(item, i, isLg))}
                    {hasMore && (
                      <Box id={overflowId} sx={{ display: "none" }} aria-hidden={true}>
                        {overflowItems.map((item, i) => renderItem(item, i + defaultVisible, isLg))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {!isLg && toggleEl && <Box sx={{ marginTop: "1rem" }}>{toggleEl}</Box>}
            </StyledPreparationCard>
          );
        })}
      </Stack>
    </StyledSectionContainer>
  );
};

export default BeforeYourSession;
